import React, { FC, useMemo } from 'react';
import { ChakraProvider } from '@chakra-ui/provider';
import { extendTheme } from '@chakra-ui/react';
import { ErrorBoundary, spTheme } from 'sp-ui';
import { getBrandColors } from 'ts/common/components/gallery/getTheme';
import { IBrandTheme, useBrandTheme, withBrandTheme } from './BrandThemeContext';

/** Sp_Brand_Theme::BACKGROUND_OPTION_DARK — the convention Nav/AboutPage/BackToTop already use. */
const BACKGROUND_OPTION_DARK = 2;

/**
 * Builds the Chakra theme the shared `ts/common/components/gallery` components need, on top of
 * sp-ui's `spTheme` (which the rest of the brand app is already themed by).
 *
 * `spTheme` has no `brand.*` palette, no `text` colour and no `Heading` component theme, so a
 * component written against the client gallery's theme — e.g. `client_react`'s `BundleFileList`
 * and its `CircleIcon` — renders unstyled under it. This supplies exactly the tokens those
 * components read and nothing else, so the brand app keeps its own typography rather than
 * adopting the gallery's.
 */
export const getBrandChakraTheme = ({ backgroundOptionId, primaryColor }: IBrandTheme) => {
    const isDarkTheme = backgroundOptionId === BACKGROUND_OPTION_DARK;

    return extendTheme(
        {
            colors: {
                // themeData.primaryColor is a bare 6-digit hex with no leading '#'
                // (Sp_Angular_AppData_Abstract::provideThemeData -> EventThemeTransformer),
                // which getBrandColors accounts for.
                brand: getBrandColors(primaryColor),
                // spTheme's gray.400 (#9FB0B7) is ~2.1:1 on white, which fails WCAG 1.4.11's
                // 3:1 for a UI boundary - and it is used as the "Helpful Tips" button border.
                gray: { 400: '#6E7173' },
                // spTheme has no `text`. navy.400 is the brand app's default font colour and is
                // ~8:1 on white; referencing the var keeps this in step with spTheme's ramp.
                text: isDarkTheme ? 'var(--chakra-colors-white)' : 'var(--chakra-colors-navy-400)'
            },
            components: {
                // Chakra's default Divider inherits its colour, which is invisible on a dark
                // brand background.
                Divider: { baseStyle: { borderColor: isDarkTheme ? '#494B4C' : '#E0E3E5' } },
                Heading: { variants: { primary: { color: 'brand.primary' } } }
            }
        },
        // extendTheme treats a trailing Chakra theme as the base rather than an override
        // (see createExtendTheme in @chakra-ui/theme-utils), so this merges onto spTheme
        // instead of onto Chakra's defaults.
        spTheme
    );
};

/**
 * Registration HOC for brand components that use Chakra plus the shared gallery components.
 *
 * Use this INSTEAD OF sp-ui's `withCommon`, not inside it: `withCommon` mounts its own
 * `ChakraProvider` from `@chakra-ui/react`, which wraps a `ToastProvider`, and Chakra's toast
 * store is a singleton — a second one replays every toast twice. `ErrorBoundary` is composed
 * here so nothing is lost by dropping `withCommon`; its `TranslateProvider` is not, because
 * these components take `translateFilter` as a prop.
 *
 * `withBrandTheme` supplies the brand theme rather than reading `spAppData` here: `spAppData.get`
 * deep-copies, so a direct read returns a new object every render and would rebuild the whole
 * Chakra theme each time. `BrandThemeProvider` freezes it, which makes the memo below hold.
 */
export const withBrandChakraTheme = <P extends object>(Component: FC<P>) =>
    withBrandTheme<P>(function WithBrandChakraTheme(props) {
        const { brandTheme } = useBrandTheme();
        const theme = useMemo(() => getBrandChakraTheme(brandTheme), [brandTheme]);

        return (
            <ErrorBoundary>
                <ChakraProvider cssVarsRoot="body" resetCSS={false} theme={theme}>
                    <Component {...(props as P)} />
                </ChakraProvider>
            </ErrorBoundary>
        );
    });
